import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';

import { Add, ArrowDownward, ArrowUpward, DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ComponentStyleProps, ImageObject } from '../../../pages/types';
import { TextBoxItemType } from '../../text-box';
import {
  validateBorderRadius,
  validateHeight,
  validateSpacing,
  validateWidth,
} from '../../admin/admin-utils';

const TEXT_ITEM_TYPES: TextBoxItemType[] = [
  'title',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle',
  'paragraph',
  'body1',
  'body2',
  'primaryButton',
  'secondaryButton',
  'button',
  'textButton',
  'divider',
];

const TEXT_ITEM_DEFAULTS: Record<TextBoxItemType, { contents: string; link?: string }> = {
  title: { contents: 'Title' },
  h1: { contents: 'Heading' },
  h2: { contents: 'Heading' },
  h3: { contents: 'Heading' },
  h4: { contents: 'Heading' },
  h5: { contents: 'Heading' },
  h6: { contents: 'Heading' },
  subtitle: { contents: 'Subtitle' },
  paragraph: { contents: 'New paragraph text.' },
  body1: { contents: 'New text.' },
  body2: { contents: 'New text.' },
  primaryButton: { contents: 'Button', link: '' },
  secondaryButton: { contents: 'Button', link: '' },
  button: { contents: 'Button', link: '' },
  textButton: { contents: 'Button', link: '' },
  divider: { contents: '' },
};

const isButtonItemType = (type: TextBoxItemType) =>
  type === 'button' ||
  type === 'primaryButton' ||
  type === 'secondaryButton' ||
  type === 'textButton';

interface ComponentOptionsPanelProps {
  type: string;
  props: Record<string, any>;
  styles: ComponentStyleProps;
  onPropChange: (key: string, value: any) => void;
  onStyleChange: (key: string, value: any) => void;
  onOpenFileSelector?: (
    callback: (result: string | string[]) => void,
    options?: { multiple?: boolean },
  ) => void;
}

const getImageUrl = (image: string | ImageObject | undefined): string => {
  if (!image) return '';
  return typeof image === 'object' ? image.url : image;
};

const SectionBox: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
      {title}
    </Typography>
    <Box display="flex" flexWrap="wrap" gap={2}>
      {children}
    </Box>
  </Box>
);

const ComponentOptionsPanel: React.FC<ComponentOptionsPanelProps> = ({
  type,
  props,
  styles,
  onPropChange,
  onStyleChange,
  onOpenFileSelector,
}) => {
  const [newItemType, setNewItemType] = useState<TextBoxItemType>('paragraph');

  const selectBackgroundImage = () => {
    if (!onOpenFileSelector) return;
    onOpenFileSelector((result) => {
      const url = Array.isArray(result) ? result[0] : result;
      onStyleChange('backgroundImage', {
        url,
        title: 'Background Image',
        altText: 'Background image',
      });
    });
  };

  // Fields validated on blur (width/height/padding/margin/borderRadius) used
  // to clear on invalid input, since an unparseable value normalized to ''
  // and '' deletes the stored key. Instead: keep whatever the user typed and
  // mark the field red, so a typo mid-edit doesn't wipe their input.
  const renderValidatedField = (
    label: string,
    styleKey: string,
    validate: (value: string) => string,
    helperText?: string,
  ) => {
    const rawValue = (styles[styleKey] as string) || '';
    const isInvalid = !!rawValue && validate(rawValue) === '';

    return (
      <TextField
        label={label}
        value={rawValue}
        onChange={(e) => onStyleChange(styleKey, e.target.value)}
        onBlur={(e) => {
          const validated = validate(e.target.value);
          if (validated) onStyleChange(styleKey, validated);
        }}
        error={isInvalid}
        fullWidth
        size="small"
        margin="dense"
        helperText={isInvalid ? 'Not a valid CSS value' : helperText}
      />
    );
  };

  const renderContentOptions = () => {
    switch (type) {
      case 'SingleImage':
        return (
          <SectionBox title="Image options">
            <Box flex="1 1 150px">
              <TextField
                label="Image Width"
                value={props.imageWidth || ''}
                onChange={(e) => onPropChange('imageWidth', e.target.value)}
                onBlur={(e) =>
                  onPropChange('imageWidth', validateWidth(e.target.value))
                }
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 100%, 500px"
              />
            </Box>
            <Box flex="1 1 150px">
              <TextField
                label="Image Height"
                value={props.imageHeight || ''}
                onChange={(e) => onPropChange('imageHeight', e.target.value)}
                onBlur={(e) =>
                  onPropChange('imageHeight', validateHeight(e.target.value))
                }
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. auto, 300px"
              />
            </Box>
            <Box
              flex="1 1 100%"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2">Show Caption</Typography>
              <Switch
                checked={props.showCaption !== false}
                onChange={(e) => onPropChange('showCaption', e.target.checked)}
              />
            </Box>
          </SectionBox>
        );

      case 'Carousel':
        return (
          <SectionBox title="Carousel options">
            <Box flex="1 1 150px">
              <TextField
                label="Carousel Height"
                value={props.carouselHeight || ''}
                onChange={(e) => onPropChange('carouselHeight', e.target.value)}
                onBlur={(e) =>
                  onPropChange(
                    'carouselHeight',
                    validateHeight(e.target.value),
                  )
                }
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 400px"
              />
            </Box>
          </SectionBox>
        );

      case 'ImageGallery':
        return (
          <SectionBox title="Gallery options">
            <Box flex="1 1 120px">
              <TextField
                label="Columns"
                type="number"
                value={props.columns ?? 3}
                onChange={(e) =>
                  onPropChange('columns', Number(e.target.value))
                }
                fullWidth
                size="small"
                margin="dense"
              />
            </Box>
            <Box flex="1 1 120px">
              <TextField
                label="Min Columns (mobile)"
                type="number"
                value={props.minColumns ?? ''}
                onChange={(e) =>
                  onPropChange(
                    'minColumns',
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                fullWidth
                size="small"
                margin="dense"
              />
            </Box>
            <Box flex="1 1 120px">
              <TextField
                label="Row Height"
                value={props.rowHeight || ''}
                onChange={(e) => onPropChange('rowHeight', e.target.value)}
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 200px"
              />
            </Box>
            <Box flex="1 1 120px">
              <TextField
                label="Gap"
                value={props.gap || ''}
                onChange={(e) => onPropChange('gap', e.target.value)}
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 8px"
              />
            </Box>
          </SectionBox>
        );

      case 'Container':
        return (
          <SectionBox title="Container options">
            <Box flex="1 1 120px">
              <TextField
                label="Columns"
                type="number"
                value={props.container?.columns ?? 1}
                onChange={(e) =>
                  onPropChange('container', {
                    ...props.container,
                    columns: Number(e.target.value),
                  })
                }
                fullWidth
                size="small"
                margin="dense"
              />
            </Box>
            <Box flex="1 1 120px">
              <TextField
                label="Spacing"
                type="number"
                value={props.container?.spacing ?? 2}
                onChange={(e) =>
                  onPropChange('container', {
                    ...props.container,
                    spacing: Number(e.target.value),
                  })
                }
                fullWidth
                size="small"
                margin="dense"
              />
            </Box>
            <Box flex="1 1 150px">
              <TextField
                label="Max Width"
                value={props.container?.maxWidth || ''}
                onChange={(e) =>
                  onPropChange('container', {
                    ...props.container,
                    maxWidth: e.target.value,
                  })
                }
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 1200px"
              />
            </Box>
          </SectionBox>
        );

      case 'Iframe':
        return (
          <SectionBox title="Iframe options">
            <Box flex="1 1 100%">
              <TextField
                label="Embed URL"
                value={props.iframeUrl || ''}
                onChange={(e) => onPropChange('iframeUrl', e.target.value)}
                fullWidth
                size="small"
                margin="dense"
              />
            </Box>
            <Box flex="1 1 150px">
              <TextField
                label="Height"
                value={props.iframeHeight || ''}
                onChange={(e) => onPropChange('iframeHeight', e.target.value)}
                onBlur={(e) =>
                  onPropChange('iframeHeight', validateHeight(e.target.value))
                }
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 400px"
              />
            </Box>
            <Box flex="1 1 150px">
              <TextField
                label="Width"
                value={props.iframeWidth || ''}
                onChange={(e) => onPropChange('iframeWidth', e.target.value)}
                onBlur={(e) =>
                  onPropChange('iframeWidth', validateWidth(e.target.value))
                }
                fullWidth
                size="small"
                margin="dense"
                helperText="e.g. 100%"
              />
            </Box>
          </SectionBox>
        );

      case 'TextBox': {
        const texts: { type: TextBoxItemType; contents: string; link?: string }[] =
          props.texts || [];

        const updateTexts = (newTexts: typeof texts) =>
          onPropChange('texts', newTexts);

        const handleItemTypeChange = (index: number, type: TextBoxItemType) => {
          const newTexts = [...texts];
          newTexts[index] = { ...newTexts[index], type };
          updateTexts(newTexts);
        };

        const handleItemLinkChange = (index: number, link: string) => {
          const newTexts = [...texts];
          newTexts[index] = { ...newTexts[index], link };
          updateTexts(newTexts);
        };

        const handleItemDelete = (index: number) => {
          updateTexts(texts.filter((_, i) => i !== index));
        };

        const handleItemMove = (index: number, direction: 'up' | 'down') => {
          const target = direction === 'up' ? index - 1 : index + 1;
          if (target < 0 || target >= texts.length) return;
          const newTexts = [...texts];
          [newTexts[index], newTexts[target]] = [newTexts[target], newTexts[index]];
          updateTexts(newTexts);
        };

        const handleAddItem = () => {
          updateTexts([
            ...texts,
            { type: newItemType, ...TEXT_ITEM_DEFAULTS[newItemType] },
          ]);
        };

        return (
          <SectionBox title="Text items">
            {texts.map((item, index) => (
              <Box
                key={index}
                flex="1 1 100%"
                sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
              >
                <Box display="flex" gap={1} alignItems="center">
                  <Select
                    value={item.type}
                    onChange={(e) =>
                      handleItemTypeChange(index, e.target.value as TextBoxItemType)
                    }
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    {TEXT_ITEM_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton
                    size="small"
                    onClick={() => handleItemMove(index, 'up')}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleItemMove(index, 'down')}
                    disabled={index === texts.length - 1}
                    title="Move down"
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleItemDelete(index)}
                    title="Delete"
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
                {isButtonItemType(item.type) && (
                  <TextField
                    label="Link"
                    value={item.link || ''}
                    onChange={(e) => handleItemLinkChange(index, e.target.value)}
                    fullWidth
                    size="small"
                    margin="dense"
                    helperText="URL, or *elementId to scroll to an element on the page"
                  />
                )}
              </Box>
            ))}

            <Box flex="1 1 100%" display="flex" gap={1} alignItems="center">
              <Select
                value={newItemType}
                onChange={(e) => setNewItemType(e.target.value as TextBoxItemType)}
                size="small"
                sx={{ flex: 1 }}
              >
                {TEXT_ITEM_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={handleAddItem}
              >
                Add
              </Button>
            </Box>
          </SectionBox>
        );
      }

      case 'Ingress':
        return (
          <SectionBox title="Ingress options">
            <Box flex="1 1 200px">
              <Typography variant="body2" mb={1}>
                Divider Line Color
              </Typography>
              <TextField
                value={props.lineColor || '#000000'}
                onChange={(e) => onPropChange('lineColor', e.target.value)}
                fullWidth
                size="small"
                margin="dense"
              />
              <HexColorPicker
                color={props.lineColor || '#000000'}
                onChange={(color) => onPropChange('lineColor', color)}
                style={{ marginTop: '8px', width: '100%', maxWidth: '220px' }}
              />
            </Box>
          </SectionBox>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 2, width: 320, maxHeight: '70vh', overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {type} Options
      </Typography>

      {renderContentOptions()}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" fontWeight="bold" mb={1}>
        Style Options
      </Typography>

      <SectionBox title="Background">
        <Box flex="1 1 100%">
          {styles.backgroundImage ? (
            <>
              <img
                src={getImageUrl(styles.backgroundImage)}
                alt="Background"
                style={{ width: '100%', maxHeight: 120, objectFit: 'cover', marginBottom: 8 }}
              />
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutline />}
                onClick={() => onStyleChange('backgroundImage', '')}
              >
                Remove Background Image
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={selectBackgroundImage}
              disabled={!onOpenFileSelector}
            >
              Select Background Image
            </Button>
          )}
        </Box>
        <Box flex="1 1 100%">
          <TextField
            label="Background Color"
            value={styles.background || ''}
            onChange={(e) => onStyleChange('background', e.target.value)}
            fullWidth
            size="small"
            margin="dense"
          />
          <HexColorPicker
            color={styles.background || '#ffffff'}
            onChange={(color) => onStyleChange('background', color)}
            style={{ marginTop: '8px', width: '100%', maxWidth: '260px' }}
          />
        </Box>
      </SectionBox>

      <SectionBox title="Text">
        <Box flex="1 1 100%">
          <TextField
            label="Text Color"
            value={styles.color || ''}
            onChange={(e) => onStyleChange('color', e.target.value)}
            fullWidth
            size="small"
            margin="dense"
            helperText="Leave empty to use the theme's default text color"
          />
          <HexColorPicker
            color={styles.color || '#000000'}
            onChange={(color) => onStyleChange('color', color)}
            style={{ marginTop: '8px', width: '100%', maxWidth: '260px' }}
          />
        </Box>
      </SectionBox>

      <SectionBox title="Layout">
        <Box flex="1 1 130px">
          {renderValidatedField('Width', 'width', validateWidth)}
        </Box>
        <Box flex="1 1 130px">
          {renderValidatedField('Height', 'height', validateHeight)}
        </Box>
        <Box flex="1 1 130px">
          {renderValidatedField('Padding', 'padding', (v) =>
            validateSpacing(v),
          )}
        </Box>
        <Box flex="1 1 130px">
          {renderValidatedField('Margin', 'margin', (v) =>
            validateSpacing(v, true),
          )}
        </Box>
        <Box flex="1 1 130px">
          {renderValidatedField(
            'Border Radius',
            'borderRadius',
            validateBorderRadius,
          )}
        </Box>
        <Box flex="1 1 130px">
          <Typography variant="body2">Text Align</Typography>
          <Select
            value={styles.textAlign || ''}
            onChange={(e) => onStyleChange('textAlign', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">Default</MenuItem>
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </Box>
      </SectionBox>
    </Box>
  );
};

export default ComponentOptionsPanel;
